// EP-136 T136-024 — the "Extend expiry" modal behind UnitBookingCard.
// PATCHes leases.reservation_expires_at via the existing useUpdateLease hook
// (admin+ only server-side, and only valid on a booking — LeasePolicy#
// extend_reservation_expiry?). Typed 422s (not_a_booking, expiry_in_past)
// are forwarded untranslated and translated here.
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Modal, Button, Input } from '@/components/ui'
import { useUpdateLease } from '@/hooks/useLeases'
import { unitKeys } from '@/hooks/useUnits'
import { propertyKeys } from '@/hooks/useProperties'
import { invalidateDetailQueries } from '@/lib/queryHelpers'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { utcDateOnly } from '@/lib/date'

export interface ExtendBookingModalProps {
  isOpen: boolean
  onClose: () => void
  leaseId: number
  /** The booking's current reservation_expires_at, ISO datetime or null. */
  currentExpiresAt: string | null
}

interface ExtendFormData {
  reservation_expires_at: string
}

// The calendar day (YYYY-MM-DD, account timezone) one day after the current
// expiry — the floor both for the date input's native `min` and for this
// modal's own pre-submit validation message. The server independently
// rejects anything not strictly in the future (422 expiry_in_past); this is
// only the UX floor, not the source of truth.
function dayAfter(isoDateTime: string, timezone: string): string {
  const d = new Date(isoDateTime)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '1')
  const next = new Date(Date.UTC(get('year'), get('month') - 1, get('day') + 1))
  return utcDateOnly(next)
}

export function ExtendBookingModal({ isOpen, onClose, leaseId, currentExpiresAt }: ExtendBookingModalProps) {
  const { t } = useTranslation('leases')
  const { t: tc } = useTranslation('common')
  const translateError = useApiErrorTranslator('leases')
  const addToast = useNotificationStore((s) => s.addToast)
  const { timezone } = useLocalizationConfig()
  const queryClient = useQueryClient()
  const updateLease = useUpdateLease()

  const minDate = currentExpiresAt ? dayAfter(currentExpiresAt, timezone) : undefined

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ExtendFormData>({
    defaultValues: { reservation_expires_at: minDate ?? '' },
  })

  // Re-seed the default whenever a different booking is targeted or the
  // modal reopens. isOpen/leaseId/currentExpiresAt/timezone are all
  // primitives, never a fresh object reference per render.
  useEffect(() => {
    if (isOpen) {
      reset({ reservation_expires_at: minDate ?? '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, leaseId, currentExpiresAt, timezone])

  const onSubmit = async (data: ExtendFormData) => {
    try {
      const updated = await updateLease.mutateAsync({
        id: leaseId,
        reservation_expires_at: data.reservation_expires_at,
      })
      // useUpdateLease's own onSuccess already refreshes the lease + its
      // customer; reservation_expires_at is additionally embedded in the
      // unit's current_booking (Api::UnitsController#show) and the
      // property's unit grid (Api::PropertiesController#show), so those
      // detail caches need their own refresh too — same "invalidate the
      // parent's embedded prefix" convention as useCreateBooking /
      // useCancelBooking.
      invalidateDetailQueries(queryClient, unitKeys.detail(updated.unit_id))
      invalidateDetailQueries(queryClient, propertyKeys.details())
      addToast({ type: 'success', message: t('booking.extend.success') })
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={t('booking.extend.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={updateLease.isPending}>
            {tc('actions.cancel')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={updateLease.isPending}>
            {t('booking.extend.action')}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        <Input
          label={t('booking.extend.date_label')}
          type="date"
          min={minDate}
          error={errors.reservation_expires_at?.message}
          {...register('reservation_expires_at', {
            required: t('booking.extend.date_required'),
            validate: (v) => !minDate || v >= minDate || t('booking.extend.date_too_early'),
          })}
        />
      </form>
    </Modal>
  )
}
